import uniqWith from "lodash/uniqWith";
import isEqual from "lodash/isEqual";
import sum from "lodash/sum";
import { v4, v4 as uuidv4 } from "uuid";
import { Tax } from "~/entities/taxEntities";
import { RoundingMethod, TaxAggMethod } from "~/entities/siteEntities";
import {
  CartLine,
  CartLineItem,
  OrderSource,
  ResponseLine,
  ResponseLineItem,
  ResponseOrder,
  ResponseOrderWithCartLines,
  SimpleItem,
} from "~/entities/orderEntities";
import { Menu, MenuTabItem } from "~/entities/menuEntities";
import {
  ChildVisibilityRule,
  Product,
  ProductChild,
} from "~/entities/productEntities";
import { sumFields } from "~/helpers/arrayHelpers";
import { autoApplyDiscounts } from "~/helpers/promotionHelpers";
import { Promotion } from "~/entities/promotionEntities";

export const choiceRequiredForProduct = (
  prod: Product,
  orderSource: OrderSource,
): boolean =>
  prod.children.some(
    (x) =>
      x.maxQty == null ||
      (x.minQty ?? 0) !== x.maxQty ||
      choiceRequiredForProduct(x.product, orderSource),
  );

export const getLineBeforeDiscountUnitPrice = (line: CartLine): number =>
  getLineBeforeDiscountPrice({ ...line, item: { ...line.item, qty: 1 } });

export const getLineBeforeDiscountPrice = (line: CartLine): number => {
  let count = 0;
  forEachLineItem(line, (lineItem, qty) => {
    count += lineItem.unitPrice * qty;
  });
  return count;
};

export const showWhenEmpty = (c: ProductChild) => {
  return (
    c.visibilityRule == ChildVisibilityRule.ALWAYS ||
    (c.visibilityRule == ChildVisibilityRule.NOT_DEFAULT &&
      getProductChildDefaultQty(c) !== 0)
  );
};

export const getProductChildDefaultQty = (c: ProductChild): number =>
  c.defaultQty ?? c.minQty ?? 0;

export const getItemShouldHide = (
  item: CartLineItem,
  parentChild: ProductChild,
): boolean => {
  return (
    item.unitPrice === 0 &&
    (item.product.hideFromCartAndKitchen ||
      (parentChild.visibilityRule === ChildVisibilityRule.NOT_DEFAULT &&
        item.qty === getProductChildDefaultQty(parentChild) &&
        item.children.length === 0))
  );
};

export const withCorrectItemShouldHide = (
  item: CartLineItem,
  parentChild: ProductChild,
): CartLineItem => ({
  ...item,
  hide: getItemShouldHide(item, parentChild),
});

export const getTotalPrice = (cartItem: CartLineItem): number => {
  return (
    cartItem.qty *
    (cartItem.unitPrice + sum(cartItem.children.map((x) => getTotalPrice(x))))
  );
};

export const getFinalPrice = (cartItem: CartLineItem): number => {
  return (
    cartItem.qty *
    (cartItem.finalUnitPrice +
      sum(cartItem.children.map((x) => getFinalPrice(x))))
  );
};

export const roundWithMethod = (method: RoundingMethod) => (amount: number) => {
  if (method === RoundingMethod.HALF_EVEN) {
    const r = amount % 1;
    if (r === 0) {
      return amount;
    }
    if (r !== 0.5) {
      return Math.round(amount);
    }
    const floor = Math.floor(amount);
    if (floor % 2 === 0) {
      return floor;
    } else {
      return floor + 1;
    }
  }

  if (method === RoundingMethod.ROUND_UP) {
    return Math.ceil(amount);
  }

  if (method === RoundingMethod.ROUND_DOWN) {
    return Math.floor(amount);
  }

  return Math.round(amount);
};

export const ONE_HUNDRED_PERCENT_TIMES_1000 = 100000;

export const percentTimes1000ToPerUnit = (percentTimes1000: number) =>
  percentTimes1000 / ONE_HUNDRED_PERCENT_TIMES_1000;

export const withCardinality = (value: number, cardinality: number) =>
  value > 0 !== cardinality > 0 ? 0 : value;

export const applyPercent = (
  amount: number,
  percentTimes1000: number,
): number => amount * percentTimes1000ToPerUnit(percentTimes1000);

export const applyPercentDiscount = (
  amount: number,
  percentTimes1000: number,
): number => amount * (1 - percentTimes1000ToPerUnit(percentTimes1000));

export const applyDiscount = (
  amount: number,
  percentTimes1000: number,
  fixedAmount: number,
) => {
  // If the fixed discount amount has opposite sign to the amount we are applying the discount to (i.e. it will make positive numbers bigger or negative smaller), then override it to zero
  const checkedFixedAmount = withCardinality(fixedAmount, amount);

  // Do calculation
  const newAmount =
    applyPercentDiscount(amount, percentTimes1000) - checkedFixedAmount;

  // If we discounted the amount by over 100% then return a final amount of 0
  return withCardinality(newAmount, amount);
};

const recurseLineItems = (
  item: CartLineItem,
  func: (
    item: CartLineItem,
    qty: number,
    set: (x: Partial<CartLineItem>) => any,
  ) => any,
  qtyMultiplier: number = 1,
): CartLineItem => {
  let ret = item;
  const qty = qtyMultiplier * item.qty;
  func(item, qty, (x) => {
    if (
      Object.keys(x).some(
        (key) => x[key as keyof typeof x] !== item[key as keyof typeof x],
      )
    ) {
      ret = { ...item, ...x };
    }
  });
  let anyChildChanged = false;
  item.children.forEach((child, childIndex) => {
    const newChild = recurseLineItems(child, func, qty);
    if (newChild !== child) {
      if (!anyChildChanged) {
        anyChildChanged = true;
        ret = { ...ret, children: ret.children.slice() };
      }
      ret.children[childIndex] = newChild;
    }
  });
  return ret;
};

export const forEachLineItem = (
  line: CartLine,
  func: (
    item: CartLineItem,
    qty: number,
    set: (x: Partial<CartLineItem>) => any,
  ) => any,
): CartLine => {
  const item = recurseLineItems(line.item, func);
  if (item !== line.item) {
    return { ...line, item };
  }
  return line;
};

export const recalculateOrder = (
  originalOrder: ResponseOrderWithCartLines,
  taxFromCode: Record<string, Tax>,
  scheduledPromotions: Promotion[],
): ResponseOrderWithCartLines => {
  // SETUP VARIABLES TO STORE CHANGES
  let order: ResponseOrder = { ...originalOrder };
  let lines = originalOrder.lines.slice();

  const round = roundWithMethod(order.taxRoundingMethod);

  // No need to calculate pricebooks or overrides as that is handled by the api in menu get

  // Recalculate promotions

  const ret = autoApplyDiscounts(order, lines, [
    ...scheduledPromotions,
    ...order.unlockedPromotions,
  ]);
  order = ret.order;
  lines = ret.lines;

  // Calculate order price before discount.

  const orderPriceBeforeDiscountOrCharges = sum(
    lines.map((line) => {
      let lineTotalBeforeDiscount = 0;
      forEachLineItem(line, (item, qty) => {
        lineTotalBeforeDiscount += item.unitPrice * qty;
      });
      return lineTotalBeforeDiscount;
    }),
  );

  // Calculate line level discounts

  let orderLineLevelDiscountedAmount = 0;

  const lineLevelDiscountMultipliers: Record<string, number> = {};

  lines = lines.map((line) => {
    let lineTotalAfterItemDiscounts = 0;
    line = forEachLineItem(line, (item, qty, set) => {
      const { itemPercentDiscount = 0, itemFixedDiscount = 0 } = sumFields(
        item.discounts.map((x) => {
          return {
            itemPercentDiscount: x.percentTimes1000,
            itemFixedDiscount: x.fixedAmount * line.item.qty,
          };
        }),
      );

      const finalUnitPrice = applyDiscount(
        item.unitPrice,
        itemPercentDiscount,
        itemFixedDiscount,
      );

      lineTotalAfterItemDiscounts += finalUnitPrice * qty;
      set({ finalUnitPrice: finalUnitPrice });
    });

    const { linePercentDiscount = 0, lineFixedDiscount = 0 } = sumFields(
      line.discounts.map((x) => {
        return {
          linePercentDiscount: x.percentTimes1000,
          lineFixedDiscount: x.fixedAmount * line.item.qty,
        };
      }),
    );

    const lineLevelDiscountedAmount = Math.max(
      0,
      applyDiscount(
        lineTotalAfterItemDiscounts,
        linePercentDiscount,
        lineFixedDiscount,
      ),
    );

    orderLineLevelDiscountedAmount += lineLevelDiscountedAmount;

    lineLevelDiscountMultipliers[line.id] =
      lineTotalAfterItemDiscounts === 0
        ? 0
        : lineLevelDiscountedAmount / lineTotalAfterItemDiscounts;

    return line;
  });

  // Skip calculating charges (handled by api)
  // Just calculate the total charge amount
  const totalChargeAmountBeforeDiscount = sum(
    order.additionalCharges.map((x) => x.amount),
  );

  // Apply order level discounts
  const { orderPercentDiscount = 0, orderFixedDiscount = 0 } = sumFields(
    order.discounts.map((x) => ({
      orderPercentDiscount: x.percentTimes1000,
      orderFixedDiscount: x.fixedAmount,
    })),
  );

  const finalOrderAmount = Math.max(
    0,
    round(
      applyDiscount(
        orderLineLevelDiscountedAmount + totalChargeAmountBeforeDiscount,
        orderPercentDiscount,
        orderFixedDiscount,
      ),
    ),
  );

  const div = orderLineLevelDiscountedAmount + totalChargeAmountBeforeDiscount;
  const orderLevelDiscountMultiplier =
    div === 0 ? 1 : Math.round(finalOrderAmount / div);

  // Apply the calculation results everywhere
  order.additionalCharges = order.additionalCharges.map((charge) => ({
    ...charge,
    finalAmount: orderLevelDiscountMultiplier * charge.amount,
  }));

  lines = lines.map((line) => {
    const discountMultiplier =
      lineLevelDiscountMultipliers[line.id] * orderLevelDiscountMultiplier;
    return forEachLineItem(line, (item, _, set) => {
      set({ finalUnitPrice: item.finalUnitPrice * discountMultiplier });
    });
  });

  order.lineTotalBeforeDiscount = orderPriceBeforeDiscountOrCharges;
  order.totalBeforeDiscount =
    orderPriceBeforeDiscountOrCharges + totalChargeAmountBeforeDiscount;
  order.finalPriceIncTax = finalOrderAmount;
  order.finalPriceExTax = finalOrderAmount;

  // Calculate the taxes

  const taxByCode: Record<string, number> = {};

  lines.forEach((line) => {
    forEachLineItem(line, (item, qty) => {
      const priceExTax =
        item.finalUnitPrice /
        (1 +
          (!order.pricesIncludeTax
            ? 0
            : percentTimes1000ToPerUnit(
                sum(
                  item.product.taxCodes.map(
                    (x) => taxFromCode[x].rateTimes1000,
                  ),
                ),
              )));

      item.product.taxCodes.forEach((taxCode) => {
        let val =
          priceExTax *
          percentTimes1000ToPerUnit(taxFromCode[taxCode].rateTimes1000);

        if (order.taxAggregationMethod === TaxAggMethod.PER_UNIT) {
          val = round(val);
        }

        val *= qty;

        if (order.taxAggregationMethod === TaxAggMethod.PER_LINE) {
          val = round(val);
        }

        // += doesn't work correctly when current value here is undefined
        taxByCode[taxCode] = val + (taxByCode[taxCode] ?? 0);
      });
    });
  });

  order.additionalCharges.forEach((charge) => {
    charge.taxes.forEach((tax) => {
      const taxRate = percentTimes1000ToPerUnit(tax.rateTimes1000);

      let val = charge.finalAmount * taxRate;

      if (order.pricesIncludeTax) {
        val /= 1 + taxRate;
      }

      if (
        [TaxAggMethod.PER_UNIT, TaxAggMethod.PER_LINE].includes(
          order.taxAggregationMethod,
        )
      ) {
        val = round(val);
      }

      // += doesn't work correctly when current value here is undefined
      taxByCode[tax.code] = val + (taxByCode[tax.code] ?? 0);
    });
  });

  Object.keys(taxByCode).forEach((code) => {
    taxByCode[code] = round(taxByCode[code]);
  });
  const totalTax = sum(Object.values(taxByCode));

  if (order.pricesIncludeTax) {
    order.finalPriceExTax -= totalTax;
  } else {
    order.finalPriceIncTax += totalTax;
  }

  return {
    ...order,
    lines,
  };
};

export const generateLine = (
  menuItem: MenuTabItem,
  menuCode: string,
): CartLine => {
  return {
    menuCode,
    id: v4(),
    discounts: [],
    item: generateTopLevelItem(menuItem.productData),
    menuItemCode: menuItem.code,
  };
};

export const generateTopLevelItem = (product: Product) =>
  genCartFor(product, false, 1);

export const generateChildItem = (
  prodChild: ProductChild,
  qty: number,
): CartLineItem => {
  const hide =
    prodChild.visibilityRule === ChildVisibilityRule.NOT_DEFAULT &&
    qty === getProductChildDefaultQty(prodChild);
  return genCartFor(prodChild.product, hide, qty);
};

const genCartFor = (product: Product, hide = false, qty = 1): CartLineItem => {
  const children: CartLineItem[] = [];
  product.children?.forEach((child) => {
    const defaultAmount = child.defaultQty ?? child.minQty ?? 0;
    if (child.product.children.length) {
      for (let i = 0; i < defaultAmount; i++) {
        // Can bypass hide check since we now the child product has children of its own
        children.push(genCartFor(child.product, false, 1));
      }
    } else if (defaultAmount || showWhenEmpty(child)) {
      children.push(generateChildItem(child, defaultAmount));
    }
  });
  return {
    id: uuidv4(),
    productCode: product.code,
    sku: product.sku,
    qty,
    children: children,
    displayName: product.name,
    unitPrice: product.price,
    finalUnitPrice: product.price,
    hide:
      (product.hideFromCartAndKitchen && !product.price) ||
      (!children.length && hide),
    product: product,
    discounts: [],
  };
};

const resItemToCart = (
  item: ResponseLineItem,
  product: Product,
): CartLineItem => {
  const children: CartLineItem[] = [];
  item.children.forEach((child) => {
    const childProduct = product.children?.find(
      (p) => p.product.code === child.productCode,
    );
    if (childProduct) {
      children.push(resItemToCart(child, childProduct.product));
    } else {
      console.warn(
        "Couldn't find menu child product, some child product items may be removed next time you modify the cart",
      );
    }
  });
  return {
    ...item,
    children,
    product,
  };
};

export const resLineToCart = (
  line: ResponseLine,
  menu: Menu,
): CartLine | null => {
  {
    // Find the product data from the menu tabs
    const menuItem = menu?.tabs
      .flatMap((tab) => tab.items)
      .find((item) =>
        line.menuItemCode
          ? item.code === line.menuItemCode
          : item.productCode === line.item.productCode,
      );

    if (!menuItem) {
      console.warn(
        `No menu item found for order line ${line.id} with product code ${line.item.productCode}`,
      );
      return null;
    }

    const prodData =
      menuItem.productCode === line.item.productCode
        ? menuItem.productData
        : menuItem.alternateProducts.find(
            (x) => x.code === line.item.productCode,
          );

    if (!prodData) {
      console.warn(
        `Menu item ${menuItem.code} does not contain product info for line ${line.id} / product ${line.item.productCode}. Your menu may have changed`,
      );
      return null;
    }

    return {
      ...line,
      item: resItemToCart(line.item, prodData),
    };
  }
};

export const getModifiersList = (
  item: CartLineItem | ResponseLineItem | SimpleItem,
): string => {
  type Child = {
    qty: number;
    name: string;
    hide: boolean;
    children: Child[];
  };

  const getNormalizeChildren = (
    children: CartLineItem[] | ResponseLineItem[] | SimpleItem[],
  ): Child[] => {
    return children.map((child) => {
      return {
        qty: child.qty,
        name:
          "product" in child
            ? child.product.name
            : "displayName" in child
              ? child.displayName
              : child.name,
        hide: child.hide,
        children: getNormalizeChildren(child.children),
      };
    });
  };

  const normalizedChildren = getNormalizeChildren(item.children);

  const getUniqChildren = (children: Child[]): Child[] => {
    return uniqWith(children, isEqual).map((child) => ({
      qty: sum(children.filter((c) => isEqual(child, c)).map((x) => x.qty)),
      name: child.name,
      hide: child.hide,
      children: child.children.length ? getUniqChildren(child.children) : [],
    }));
  };

  const uniqChildren = getUniqChildren(normalizedChildren);

  const list: {
    qty: number;
    name: string;
    deep: number;
  }[] = [];

  const initList = (children: Child[], deep: number) => {
    children.forEach((child) => {
      if (!child.hide) {
        list.push({ qty: child.qty, name: child.name, deep });
      }

      if (child.children.length) {
        initList(child.children, child.hide ? deep : deep + 1);
      }
    });
  };

  initList(uniqChildren, 1);

  return list.reduce((str, modifier, index) => {
    const qty =
      modifier.qty === 0
        ? "No\xa0"
        : modifier.qty === 1
          ? ""
          : `${modifier.qty}\xa0×\xa0`;
    const name = `${qty}${modifier.name}`;
    const separator =
      list[index + 1]?.deep === 1 ? "\n" : list[index + 1] ? ", " : "";
    return `${str}${name}${separator}`;
  }, "");
};

export const cartIsValid = (cart: CartLineItem): boolean => {
  const childCount = sum(cart.children.map((x) => x.qty));

  if (childCount < (cart.product.minChildren || 0)) {
    return false;
  }

  if (
    cart.product.maxChildren != null &&
    cart.product.maxChildren < childCount
  ) {
    return false;
  }

  if (cart.qty > 0 && cart.product.soldOut) {
    return false;
  }

  return cart.children.every((child) => cartIsValid(child));
};

export const countCartProducts = (
  counts: Record<string, number>,
  cartItem: CartLineItem,
  qty: number,
) => {
  if (counts[cartItem.product.code] == null) {
    counts[cartItem.product.code] = 0;
  }
  counts[cartItem.product.code] += qty;
  cartItem.children.forEach((x) => countCartProducts(counts, x, qty * x.qty));
};

export const getProductCount = (
  counts: Record<string, number>,
  productCode: string,
) => counts[productCode] ?? 0;

/**
 * Number of items in an order, counting quantities rather than lines - adding a
 * simple product that is already in the cart bumps an existing line's qty
 * instead of adding a new one (see addOrderLine).
 *
 * Structurally typed so it takes both cart lines and response lines.
 */
export const getCartItemCount = (lines: { item: { qty: number } }[]) =>
  lines.reduce((sum, line) => sum + line.item.qty, 0);
